import { alpha } from '@mui/material/styles';

const CHILD_ACCENT_SOFT_ALPHA = 0.08;
const CHILD_ACCENT_WASH_ALPHA = 0.025;

export const CHILD_ACCENT_PALETTE = [
  { key: 'sage', primary: '#A9D3C7', ink: '#2F6658' },
  { key: 'violet', primary: '#B8ADDA', ink: '#4A3A91' },
  { key: 'rose', primary: '#D9A9BE', ink: '#873B62' },
  { key: 'gold', primary: '#E2C389', ink: '#83591A' },
  { key: 'blue', primary: '#A5C5DF', ink: '#2D5D91' },
  { key: 'olive', primary: '#BAC79A', ink: '#526426' },
  { key: 'coral', primary: '#E0AA9C', ink: '#8A4232' },
  { key: 'slate', primary: '#AEB7C7', ink: '#394761' },
  { key: 'teal', primary: '#9FC9CE', ink: '#2D6A71' },
  { key: 'plum', primary: '#CEABCE', ink: '#744074' },
];

const PALETTE_BY_KEY = CHILD_ACCENT_PALETTE.reduce((lookup, accent) => ({
  ...lookup,
  [accent.key]: accent,
}), {});

const softenAccentBackgrounds = (accent) => ({
  ...accent,
  soft: alpha(accent.primary, CHILD_ACCENT_SOFT_ALPHA),
  wash: alpha(accent.primary, CHILD_ACCENT_WASH_ALPHA),
});

const hashString = (value = '') => (
  String(value || '').split('').reduce((hash, character) => (
    ((hash * 31) + character.charCodeAt(0)) >>> 0
  ), 0)
);

const isPaletteKey = (key) => Boolean(PALETTE_BY_KEY[key]);

export const getNextChildAccentKey = (children = []) => {
  const usedKeys = new Set(children
    .map((child) => String(child?.profileColorKey || '').trim().toLowerCase())
    .filter(isPaletteKey));
  return CHILD_ACCENT_PALETTE.find((accent) => !usedKeys.has(accent.key))?.key
    || CHILD_ACCENT_PALETTE[children.length % CHILD_ACCENT_PALETTE.length].key;
};

export const assignMissingChildAccentKeys = (children = []) => {
  const usedKeys = new Set(children
    .map((child) => String(child?.profileColorKey || '').trim().toLowerCase())
    .filter(isPaletteKey));
  let overflowIndex = 0;

  return children.map((child) => {
    const savedKey = String(child?.profileColorKey || '').trim().toLowerCase();
    if (isPaletteKey(savedKey)) {
      return child;
    }

    const availableAccent = CHILD_ACCENT_PALETTE.find((accent) => !usedKeys.has(accent.key));
    const profileColorKey = availableAccent?.key
      || CHILD_ACCENT_PALETTE[overflowIndex++ % CHILD_ACCENT_PALETTE.length].key;
    usedKeys.add(profileColorKey);
    return { ...child, profileColorKey };
  });
};

export const getChildAccentTheme = (child = {}) => {
  const explicitKey = String(child.profileColorKey || child.accentColorKey || child.colorKey || '').trim().toLowerCase();
  if (explicitKey && PALETTE_BY_KEY[explicitKey]) {
    return softenAccentBackgrounds(PALETTE_BY_KEY[explicitKey]);
  }

  const explicitColor = String(child.profileColor || child.accentColor || '').trim();
  if (explicitColor) {
    return softenAccentBackgrounds({
      key: 'custom',
      primary: explicitColor,
      ink: explicitColor,
    });
  }

  const key = child.id || child.name || 'default-child';
  return softenAccentBackgrounds(CHILD_ACCENT_PALETTE[hashString(key) % CHILD_ACCENT_PALETTE.length]);
};

export const getChildInitial = (child = {}) => (
  String(child.name || '?').trim().charAt(0).toUpperCase() || '?'
);
